import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle, Crown, Rocket, Buildings, Sparkle, ArrowRight, Lightning,
  Warning, Receipt, DownloadSimple, ArrowCounterClockwise, CalendarBlank,
  Clock, ShieldCheck, Info, Timer, IdentificationCard, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_ICONS  = { free: Sparkle, basic: Timer, starter: Rocket, pro: Crown, agency: Buildings };
const PLAN_COLORS = {
  free:    { bg: "bg-slate-50 dark:bg-slate-800",      border: "border-slate-200 dark:border-slate-600",   btn: "bg-slate-700 hover:bg-slate-800",   badge: "bg-slate-100 dark:bg-slate-700 text-slate-500",      ring: "ring-slate-300" },
  basic:   { bg: "bg-teal-50 dark:bg-teal-900/20",     border: "border-teal-200 dark:border-teal-700",     btn: "bg-teal-500 hover:bg-teal-600",     badge: "bg-teal-100 dark:bg-teal-900/50 text-teal-600",      ring: "ring-teal-300" },
  starter: { bg: "bg-blue-50 dark:bg-blue-900/20",     border: "border-blue-200 dark:border-blue-700",     btn: "bg-blue-500 hover:bg-blue-600",     badge: "bg-blue-100 dark:bg-blue-900/50 text-blue-600",      ring: "ring-blue-300" },
  pro:     { bg: "bg-amber-50 dark:bg-amber-900/20",   border: "border-amber-300 dark:border-amber-700",   btn: "bg-amber-400 hover:bg-amber-500",   badge: "bg-amber-100 dark:bg-amber-900/50 text-amber-600",   ring: "ring-amber-300" },
  agency:  { bg: "bg-violet-50 dark:bg-violet-900/20", border: "border-violet-300 dark:border-violet-700", btn: "bg-violet-500 hover:bg-violet-600", badge: "bg-violet-100 dark:bg-violet-900/50 text-violet-600", ring: "ring-violet-300" },
};
const PLAN_FEATURES = {
  free:    ["1 property · 1 iCal feed", "20 AI smart replies/mo", "Booking calendar & task board", "GST dashboard & liability calculator", "Guest Guidebook", "Setup Advisor & Legal Hub", "1-month free trial"],
  basic:   ["1 property · 1 iCal feed", "Unlimited AI smart replies", "Booking calendar & task board", "GST dashboard & GSTR-1 export", "Guest Guidebook", "Setup Advisor & Legal Hub"],
  starter: ["3 properties · 3 iCal feeds", "Unlimited AI smart replies", "Channel Manager (incl. per-OTA pricing) — push to 20+ OTAs", "AI Dynamic Pricing", "WhatsApp automations", "Guest Guidebook", "Property ROI Evaluator", "GST-ready P&L reports"],
  pro:     ["10 properties · 10 iCal feeds", "Unlimited AI smart replies", "Team access", "Priority support", "Everything in Starter"],
  agency:  ["Unlimited properties & iCal feeds", "Unlimited AI smart replies", "White-label — coming soon", "Dedicated account manager", "Consolidated multi-property P&L", "Everything in Pro"],
};

function fmt(n) {
  if (n === -1) return "Unlimited";
  try { return (n ?? 0).toLocaleString("en-IN"); } catch { return String(n ?? 0); }
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function daysUntil(iso) {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));
}

export default function Billing() {
  const { user, checkAuth } = useAuth();

  const [plans, setPlans]       = useState({});
  const [current, setCurrent]   = useState(null);
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [paying, setPaying]     = useState(null);
  const [showCancel, setShowCancel]   = useState(false);
  const [cancelling, setCancelling]   = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [spPaying, setSpPaying]       = useState(false);
  const [panInput, setPanInput]   = useState("");
  const [panSaving, setPanSaving] = useState(false);

  const refresh = async () => {
    const [p, c, h] = await Promise.all([
      fetch(`${API}/billing/plans`,   { credentials: "include" }).then(r => r.json()),
      fetch(`${API}/billing/current`, { credentials: "include" }).then(r => r.json()),
      fetch(`${API}/billing/history`, { credentials: "include" }).then(r => r.json()).catch(() => ({ history: [] })),
    ]);
    setPlans(p.plans || {});
    setCurrent(c);
    setHistory(h.history || []);
  };

  useEffect(() => { refresh().finally(() => setLoading(false)); }, []); // eslint-disable-line

  const handleUpgrade = async (planId) => {
    if (planId === "free") return; // trial is free — no payment
    // Use subscription (auto-renewal) flow if Razorpay plan ID is configured for this plan
    if (plans.subscriptions_enabled && plans.subscription_plan_ids?.includes(planId)) {
      return handleSubscribe(planId);
    }
    // One-time payment flow
    setPaying(planId);
    try {
      const orderRes = await fetch(`${API}/billing/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan_id: planId }),
      });
      if (!orderRes.ok) throw new Error((await orderRes.json()).detail);
      const order = await orderRes.json();

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "HostFolio",
        description: `${order.plan.name} Plan — Monthly`,
        order_id: order.order_id,
        handler: async (response) => {
          const verifyRes = await fetch(`${API}/billing/verify-payment`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            }),
          });
          if (verifyRes.ok) {
            toast.success(`${order.plan.name} plan activated!`);
            await checkAuth();
            await refresh();
          } else {
            toast.error("Payment verification failed. Contact support@hostfolio.in");
          }
          setPaying(null);
        },
        modal: { ondismiss: () => setPaying(null) },
        prefill: { name: user?.name, email: user?.email },
        theme: { color: "#F59E0B" },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r.error.description || "Payment failed. Please try again.");
        setPaying(null);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.message || "Could not initiate payment.");
      setPaying(null);
    }
  };

  const handleSubscribe = async (planId) => {
    setPaying(planId);
    try {
      const res = await fetch(`${API}/billing/create-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan_id: planId }),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      const data = await res.json();

      const startsAt = data.starts_at ? fmtDate(data.starts_at) : null;
      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "HostFolio",
        description: startsAt
          ? `${data.plan.name} Plan — auto-pay, first charge ${startsAt}`
          : `${data.plan.name} Plan — Auto-renewal`,
        handler: async () => {
          toast.success(startsAt
            ? `Auto-pay set up! Your ${data.plan.name} plan continues free until ${startsAt}, then billing begins.`
            : `${data.plan.name} plan activated! Auto-renews monthly.`);
          await checkAuth();
          await refresh();
          setPaying(null);
        },
        modal: { ondismiss: () => setPaying(null) },
        prefill: { name: user?.name, email: user?.email },
        theme: { color: "#F59E0B" },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r.error.description || "Payment failed. Please try again.");
        setPaying(null);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.message || "Could not initiate subscription.");
      setPaying(null);
    }
  };

  const handleSmartPricingSubscribe = async () => {
    setSpPaying(true);
    try {
      const res = await fetch(`${API}/billing/smart-pricing/subscribe`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      const data = await res.json();
      if (data.already_active) {
        toast.success("Smart Pricing auto-pay is already active — quantity updated.");
        await refresh(); setSpPaying(false); return;
      }
      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "HostFolio",
        description: `Smart Pricing — ₹${data.amount_inr.toLocaleString("en-IN")}/mo (${data.quantity} propert${data.quantity === 1 ? "y" : "ies"})`,
        handler: async () => {
          toast.success("Smart Pricing auto-pay activated!");
          await refresh(); setSpPaying(false);
        },
        modal: { ondismiss: () => setSpPaying(false) },
        prefill: { name: user?.name, email: user?.email },
        theme: { color: "#F59E0B" },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r.error.description || "Payment failed. Please try again.");
        setSpPaying(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.message || "Could not set up Smart Pricing auto-pay.");
      setSpPaying(false);
    }
  };

  const handleCancelSmartPricing = async () => {
    if (!window.confirm("Cancel Smart Pricing auto-pay? Dynamic pricing will stop for all your properties and rates will freeze at their current values.")) return;
    setSpPaying(true);
    try {
      const res = await fetch(`${API}/billing/smart-pricing/cancel`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success("Smart Pricing cancelled. Dynamic pricing has been switched off.");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Could not cancel Smart Pricing.");
    } finally {
      setSpPaying(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await fetch(`${API}/billing/cancel`, { method: "POST", credentials: "include" });
      toast.success("Plan cancellation scheduled. Access continues for 30 days.");
      await checkAuth();
      await refresh();
      setShowCancel(false);
    } catch {
      toast.error("Could not cancel. Try again.");
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await fetch(`${API}/billing/reactivate`, { method: "POST", credentials: "include" });
      toast.success("Cancellation reversed — your plan continues!");
      await checkAuth();
      await refresh();
    } catch {
      toast.error("Could not reactivate. Try again.");
    } finally {
      setReactivating(false);
    }
  };

  const activateTrial = async () => {
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(panInput)) {
      toast.error("Invalid PAN format. Example: ABCDE1234F");
      return;
    }
    setPanSaving(true);
    try {
      const res = await fetch(`${API}/billing/activate-trial`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pan: panInput }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success("Trial activated! Enjoy 1 month free.");
      await refresh();
      setPanInput("");
    } catch (e) {
      toast.error(e.message || "Could not activate trial.");
    } finally {
      setPanSaving(false);
    }
  };

  const downloadInvoice = (orderId) => {
    const token = localStorage.getItem("session_token");
    const url = `${API}/billing/invoice/${orderId}${token ? `?token=${token}` : ""}`;
    window.open(url, "_blank");
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="spinner" />
    </div>
  );

  const currentPlanId   = current?.plan_id || "free";
  const effectivePlanId = current?.effective_plan_id || currentPlanId;
  const isCancelling    = !!current?.plan_cancels_at;
  const planExpired     = current?.plan_expired;           // paid plan whose period has ended
  const isSubscription  = current?.is_subscription;
  const daysLeft        = current?.days_remaining;
  const cancelDays      = daysUntil(current?.plan_cancels_at);
  const trialDaysLeft   = current?.trial_days_left;
  const trialExpired    = current?.trial_expired;
  const trialPanSet     = current?.trial_pan_set;
  const isOnTrial       = currentPlanId === "free";
  const paidTrial       = !!current?.is_trial;                 // paid-tier trial (e.g. Pro on an admin-granted trial)
  const paidTrialDays   = current?.trial_days_remaining ?? null;

  if (user?.is_staff) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <Crown size={40} className="text-slate-300 mb-4" />
        <h2 className="font-manrope text-xl font-bold text-slate-700 dark:text-slate-200">Not accessible</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">Billing is managed by the account owner.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h1 className="font-manrope text-2xl font-bold text-slate-900 dark:text-amber-400 tracking-tight">Plans & Billing</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Manage your subscription, view invoices, and upgrade your plan.</p>
      </div>

      {/* ── Current Plan Banner ── */}
      {current && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl border-2 p-5 space-y-4 ${
            isCancelling
              ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700"
              : "bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/10 border-amber-200 dark:border-amber-700"
          }`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-1">Current Plan</p>
              <p className="font-bold text-slate-800 dark:text-white text-xl capitalize">{current.plan?.name || currentPlanId}</p>
              {currentPlanId === "free" ? (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Free trial — no credit card required</p>
              ) : paidTrial ? (
                <div className="flex items-center gap-1.5 mt-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                  <Sparkle size={12} weight="fill" />
                  Free trial · {paidTrialDays ?? "—"} day{paidTrialDays === 1 ? "" : "s"} left
                  <span className="font-normal text-slate-500 dark:text-slate-400">· full access until {fmtDate(current.plan_expires_at)}</span>
                </div>
              ) : isCancelling ? (
                <div className="flex items-center gap-1.5 mt-1 text-xs text-amber-700 dark:text-amber-400">
                  <Warning size={12} weight="fill" />
                  Cancels {fmtDate(current.plan_cancels_at)} · {cancelDays} day{cancelDays !== 1 ? "s" : ""} remaining
                </div>
              ) : isSubscription ? (
                <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500 dark:text-slate-400">
                  <ArrowsClockwise size={12} className="text-green-500" />
                  Auto-renews {fmtDate(current.plan_expires_at)}
                  {daysLeft !== null && daysLeft <= 7 && (
                    <span className="ml-1 text-amber-600 font-semibold">({daysLeft}d left)</span>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500 dark:text-slate-400">
                  <CalendarBlank size={12} />
                  Renews {fmtDate(current.plan_expires_at)}
                  {daysLeft !== null && daysLeft <= 7 && (
                    <span className="ml-1 text-amber-600 font-semibold">({daysLeft}d left)</span>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-6">
              {[
                { label: "Properties", used: current.properties_used, limit: current.plan?.properties },
                { label: "AI Replies", used: current.ai_replies_used, limit: current.plan?.ai_replies },
              ].map(({ label, used, limit }) => (
                <div key={label} className="text-center min-w-[80px]">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">{label}</p>
                  <p className="font-bold text-slate-800 dark:text-white text-sm">
                    {used} <span className="text-slate-400 font-normal text-xs">/ {fmt(limit)}</span>
                  </p>
                  {limit !== -1 && limit > 0 && (
                    <div className="w-20 h-1.5 bg-amber-100 dark:bg-amber-900/40 rounded-full mt-1.5 mx-auto">
                      <div className="h-full bg-amber-400 rounded-full transition-all"
                        style={{ width: `${Math.min(100, (used / limit) * 100)}%` }} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {currentPlanId !== "free" && (
              isCancelling ? (
                <button onClick={handleReactivate} disabled={reactivating}
                  className="flex items-center gap-1.5 text-xs bg-white dark:bg-slate-700 border border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 px-3 py-2 rounded-xl font-semibold transition disabled:opacity-50">
                  <ArrowCounterClockwise size={13} />
                  {reactivating ? "Reactivating…" : "Undo Cancellation"}
                </button>
              ) : (
                <button onClick={() => setShowCancel(true)}
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors self-center">
                  Cancel plan
                </button>
              )
            )}
          </div>

          {/* Paid-tier trial countdown */}
          {paidTrial && (
            <div className="flex flex-wrap items-center gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-xl px-4 py-3">
              <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-100 dark:bg-emerald-800/40 shrink-0">
                <Timer size={20} weight="duotone" className="text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                  {paidTrialDays ?? "—"} day{paidTrialDays === 1 ? "" : "s"} left in your free trial
                </p>
                <p className="text-xs text-emerald-600/80 dark:text-emerald-400/70">
                  Full {current.plan?.name} access until {fmtDate(current.plan_expires_at)}. Set up auto-pay to keep it running after — you won't be charged until the trial ends.
                </p>
              </div>
            </div>
          )}

          {/* Grace period info banner */}
          {isCancelling && (
            <div className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-xl px-3 py-2.5">
              <Info size={13} className="shrink-0 mt-0.5" />
              <span>
                Your plan is active until <strong>{fmtDate(current.plan_cancels_at)}</strong>. All your properties and data are safe.
                After that, you'll move to the Free plan (1 property, 20 AI replies). You can undo this anytime before then.
              </span>
            </div>
          )}

          {/* Smart Pricing add-on */}
          {current.smart_pricing_count > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs bg-white/60 dark:bg-slate-800/40 border border-amber-200 dark:border-amber-700/60 rounded-xl px-3 py-2.5">
              <span className="text-slate-600 dark:text-slate-300">
                <strong className="text-slate-800 dark:text-white">Smart Pricing</strong> add-on · {current.smart_pricing_count} propert{current.smart_pricing_count === 1 ? "y" : "ies"}
                <span className="ml-1.5 font-semibold text-slate-800 dark:text-white">+₹{current.smart_pricing_addon_inr.toLocaleString("en-IN")}/mo</span>
              </span>
              {current.smart_pricing_subscription_active ? (
                <span className="flex items-center gap-2">
                  <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
                    <CheckCircle size={12} weight="fill" /> Auto-pay active
                  </span>
                  <button onClick={handleCancelSmartPricing} disabled={spPaying}
                    className="text-slate-400 hover:text-red-500 underline underline-offset-2 disabled:opacity-50">
                    Cancel
                  </button>
                </span>
              ) : current.smart_pricing_billing_enabled ? (
                <button
                  onClick={handleSmartPricingSubscribe}
                  disabled={spPaying}
                  className="bg-amber-400 hover:bg-amber-500 text-slate-900 font-semibold px-3 py-1.5 rounded-lg transition disabled:opacity-50"
                >
                  {spPaying ? "Setting up…" : "Set up auto-pay"}
                </button>
              ) : (
                <span className="text-slate-400">billed separately</span>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* ── Cancel Confirmation Modal ── */}
      <AnimatePresence>
        {showCancel && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-800 rounded-2xl p-5 space-y-4 shadow-lg">
            <div className="flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-red-500" />
              <p className="font-semibold text-slate-800 dark:text-white">Cancel your {current?.plan?.name} plan?</p>
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300 space-y-2">
              <p>You won't be charged again. Your plan stays fully active for <strong>30 more days</strong>, then automatically downgrades to Free.</p>
              <ul className="list-disc list-inside space-y-1 text-slate-500 dark:text-slate-400 text-xs">
                <li>All your data (properties, bookings, expenses) is kept — nothing is deleted</li>
                <li>You can reactivate anytime during the 30-day window</li>
                <li>After 30 days: limited to 1 property and 20 AI replies/month</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <button onClick={handleCancel} disabled={cancelling}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-semibold rounded-xl py-2.5 transition">
                {cancelling ? "Cancelling…" : "Yes, cancel plan"}
              </button>
              <button onClick={() => setShowCancel(false)}
                className="flex-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm font-semibold rounded-xl py-2.5 transition">
                Keep my plan
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Paid Plan Expired Banner ── */}
      {planExpired && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-2xl p-5 flex items-start gap-3">
          <Warning size={20} weight="fill" className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800 dark:text-red-300">
              Your {current?.plan?.name} plan has expired
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-0.5">
              Properties over your plan limit are now read-only. Renew to restore full access,
              or properties will remain visible but cannot accept new bookings.
            </p>
          </div>
        </motion.div>
      )}

      {/* ── Trial Expired Banner ── */}
      {isOnTrial && trialExpired && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-2xl p-5 flex items-start gap-3">
          <Warning size={20} weight="fill" className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800 dark:text-red-300">Your 1-month trial has ended</p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-0.5">
              Your data is safe. Continue on the <strong>Basic plan at ₹1,149/mo</strong> (same 1 property) or upgrade to Starter for 3 properties and AI pricing.
            </p>
          </div>
        </motion.div>
      )}

      {/* ── Trial countdown / PAN activation ── */}
      {isOnTrial && !trialExpired && (
        <motion.div id="trial-pan-panel" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Timer size={18} className="text-teal-600" weight="duotone" />
              <div>
                <p className="text-sm font-semibold text-teal-800 dark:text-teal-300">
                  {trialPanSet
                    ? `Trial active · ${trialDaysLeft ?? "?"} day${trialDaysLeft !== 1 ? "s" : ""} remaining`
                    : "Activate your free trial"}
                </p>
                <p className="text-xs text-teal-600 dark:text-teal-400">
                  {trialPanSet
                    ? `Ends ${fmtDate(current?.trial_ends_at)} · 1 month free, 1 PAN per person`
                    : "Enter your PAN to start your 1-month free trial. One trial per PAN — no abuse, no gimmicks."}
                </p>
              </div>
            </div>
            {trialPanSet && trialDaysLeft !== null && trialDaysLeft <= 14 && (
              <span className="text-[11px] bg-amber-100 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-full font-semibold">
                Expiring soon — consider upgrading
              </span>
            )}
          </div>
          {!trialPanSet && (
            <div className="flex gap-2">
              <div className="relative flex-1">
                <IdentificationCard size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={panInput}
                  onChange={e => setPanInput(e.target.value.toUpperCase().slice(0, 10))}
                  placeholder="ABCDE1234F"
                  className="w-full pl-9 pr-4 py-2.5 text-sm font-mono border border-teal-200 dark:border-teal-700 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 rounded-xl focus:outline-none focus:border-teal-400 uppercase"
                />
              </div>
              <button onClick={activateTrial} disabled={panSaving || panInput.length !== 10}
                className="px-4 py-2.5 bg-teal-500 hover:bg-teal-600 disabled:opacity-40 text-white text-sm font-semibold rounded-xl transition">
                {panSaving ? "Verifying…" : "Activate Trial"}
              </button>
            </div>
          )}
        </motion.div>
      )}

      {/* ── Plan Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4">
        {Object.entries(plans).filter(([id]) => id !== "admin" && (id !== "basic" || currentPlanId === "basic")).map(([planId, plan], i) => {
          const c    = PLAN_COLORS[planId] || PLAN_COLORS.free;
          const Icon = PLAN_ICONS[planId]  || Sparkle;
          const isCurrent = currentPlanId === planId;
          const isFree    = planId === "free"; // trial is always free — don't rely on backend price_inr
          const features  = (PLAN_FEATURES[planId] || []).filter(f => !f.includes("trial"));

          const BTN_LABEL = {
            free: "Start Free Trial", starter: "Get Starter",
            pro: "Get Pro", agency: "Get Agency",
          }[planId] || `Get ${plan.name}`;

          return (
            <motion.div key={planId}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              className={`relative bg-white dark:bg-slate-800 rounded-2xl border-2 ${c.border} p-5 flex flex-col gap-4
                transition-all duration-200 hover:-translate-y-1 hover:shadow-xl
                ${planId === "pro" ? "shadow-lg shadow-amber-100 dark:shadow-amber-900/20" : "shadow-sm"}
                ${isCurrent ? `ring-2 ${c.ring}` : `hover:ring-2 ${c.ring}`}`}>

              {/* Top badges */}
              {planId === "free" && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-green-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow whitespace-nowrap">1 MONTH FREE</span>
                </div>
              )}
              {planId === "pro" && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-amber-400 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow whitespace-nowrap">MOST POPULAR</span>
                </div>
              )}
              {isCurrent && (
                <div className="absolute top-3 right-3">
                  <CheckCircle size={18} weight="fill" className="text-green-500" />
                </div>
              )}

              {/* Icon + name + price */}
              <div className="flex items-center gap-3 mt-1">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.badge}`}>
                  <Icon size={20} weight="duotone" />
                </div>
                <div>
                  <p className="font-bold text-slate-800 dark:text-white">{plan.name}</p>
                  <p className="text-sm font-semibold">
                    {isFree
                      ? <span className="text-green-600">Free for 1 month, then ₹1,149/mo</span>
                      : planId === "agency"
                        ? <span className="text-violet-600 dark:text-violet-400">Custom pricing</span>
                        : <span className="text-slate-700 dark:text-slate-200">
                            ₹{fmt(plan.price_inr ?? 0)}
                            <span className="text-xs font-normal text-slate-400">/mo</span>
                          </span>
                    }
                  </p>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-1.5 flex-1">
                {features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                    <CheckCircle size={12} weight="fill" className="text-green-400 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {isCurrent ? (
                <div className="w-full text-center py-2.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-400 text-sm font-semibold">
                  {isCancelling ? "Active until " + fmtDate(current?.plan_cancels_at) : "Current Plan ✓"}
                </div>
              ) : isFree ? (
                /* Trial — no payment; show only for trial users who haven't activated yet */
                isOnTrial && !trialPanSet ? (
                  <button onClick={() => document.getElementById("trial-pan-panel")?.scrollIntoView({ behavior: "smooth" })}
                    className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 dark:bg-slate-600 dark:hover:bg-slate-500 text-white text-sm font-bold transition">
                    Activate Free Trial
                  </button>
                ) : null
              ) : planId === "agency" ? (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText("support@hostfolio.in").catch(() => {});
                    toast.success("Email copied: support@hostfolio.in", { duration: 4000 });
                  }}
                  className={`w-full py-2.5 rounded-xl text-white text-sm font-bold transition flex items-center justify-center gap-1.5 ${c.btn}`}>
                  <ArrowRight size={13} weight="bold" />
                  Get in touch
                </button>
              ) : (
                <button onClick={() => handleUpgrade(planId)} disabled={!!paying}
                  className={`w-full py-2.5 rounded-xl text-white text-sm font-bold transition ${c.btn} disabled:opacity-50`}>
                  {paying === planId
                    ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin mx-auto" />
                    : plans.subscriptions_enabled && plans.subscription_plan_ids?.includes(planId)
                      ? <span className="flex items-center justify-center gap-1.5"><ArrowsClockwise size={13} weight="bold" />{BTN_LABEL}</span>
                      : BTN_LABEL
                  }
                </button>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* ── What's included note ── */}
      <div className="flex items-start gap-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl px-5 py-4 text-xs text-slate-500 dark:text-slate-400">
        <ShieldCheck size={16} className="text-amber-400 shrink-0 mt-0.5" />
        <span>All plans include: iCal import, booking calendar, GST dashboard, expense tracking, P&L reports, task management, direct booking link, Guest Guidebook, Setup Advisor, and Legal Guide.
        GSTR-1 export (filing-ready ZIP) available on all paid plans.
        Payments secured by <strong className="text-slate-600 dark:text-slate-300">Razorpay</strong> · Cancel anytime · 30-day data retention after cancellation.</span>
      </div>

      {/* ── Payment History ── */}
      {/* Note: outer div intentionally has no overflow-hidden — it prevents overflow-x-auto from scrolling on iOS Safari */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm">
        <div className="px-5 py-4 border-b border-slate-50 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt size={16} className="text-amber-500" weight="duotone" />
            <h2 className="text-sm font-semibold text-slate-800 dark:text-white">Payment History</h2>
          </div>
          <p className="text-xs text-slate-400">Last 20 transactions</p>
        </div>

        {history.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Clock size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No payments yet.</p>
          </div>
        ) : (
          <>
            {/* Mobile card view (hidden on md+) */}
            <div className="md:hidden divide-y divide-slate-50 dark:divide-slate-700/50">
              {history.map((h, i) => (
                <motion.div key={h.order_id}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.03 }}
                  className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{h.plan_name} Plan</span>
                      <span className="text-xs font-bold font-mono text-slate-700 dark:text-slate-200">₹{(h.amount_inr || 0).toLocaleString("en-IN")}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{fmtDate(h.paid_at)}</p>
                    <p className="text-[10px] font-mono text-slate-300 dark:text-slate-600 truncate mt-0.5">{h.payment_id || h.order_id}</p>
                  </div>
                  <button onClick={() => downloadInvoice(h.order_id)}
                    className="shrink-0 flex items-center gap-1 text-amber-600 hover:text-amber-700 dark:text-amber-400 text-xs font-semibold transition">
                    <DownloadSimple size={14} weight="bold" /> PDF
                  </button>
                </motion.div>
              ))}
            </div>

            {/* Desktop table view (hidden below md) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-700/60 text-slate-400 uppercase tracking-wide text-[10px]">
                    <th className="px-5 py-2.5 text-left font-semibold">Date</th>
                    <th className="px-5 py-2.5 text-left font-semibold">Plan</th>
                    <th className="px-5 py-2.5 text-left font-semibold">Payment ID</th>
                    <th className="px-5 py-2.5 text-right font-semibold">Amount</th>
                    <th className="px-5 py-2.5 text-center font-semibold">Invoice</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-700/50">
                  {history.map((h, i) => (
                    <motion.tr key={h.order_id}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                      <td className="px-5 py-3 text-slate-700 dark:text-slate-200">{fmtDate(h.paid_at)}</td>
                      <td className="px-5 py-3">
                        <span className="font-medium text-slate-800 dark:text-slate-100">{h.plan_name}</span>
                        <span className="ml-1.5 text-slate-400">Plan</span>
                      </td>
                      <td className="px-5 py-3 font-mono text-slate-400 dark:text-slate-500 max-w-[160px] truncate">{h.payment_id || h.order_id}</td>
                      <td className="px-5 py-3 font-mono text-right font-semibold text-slate-800 dark:text-slate-100">
                        ₹{(h.amount_inr || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <button onClick={() => downloadInvoice(h.order_id)}
                          className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 dark:text-amber-400 font-medium transition">
                          <DownloadSimple size={13} weight="bold" /> PDF
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
